<template>
  <div class="pa-0 ma-0 fill-height" style="background-color:#f5f5f5" fluid>
    <div>
      <v-container class="fill-height pl-1 pr-0 mx-1 py-2 mt-0" fluid>
        <v-row align="center" justify="center">
          <v-col cols="12" class="pt-8">
            <div
              style="max-width:1200px; display: block; margin:auto !important;"
            >
              <p
                style="text-align:center;"
                class="display-1 font-weight-medium"
              >
                Buyer Directory
              </p>
              <p style="text-align:center;" class="body-1 font-weight-normal">
                Buyer profiles offer insights into a specific entity, including
                award trends over time, the top categories the buyer has directed spending towards, and the suppliers to the entity.
              </p>
              <v-row align="start" justify="center" class="align-top-row">
                <!-- COFOG Classification Filter -->
                <v-col cols="4" class="pa-0 ma-0 d-flex align-start">
                  <v-combobox
                    v-model="selectedCofogs"
                    :items="cofog_options_formatted"
                    item-text="text"
                    item-value="value"
                    return-object
                    x-small-chips
                    multiple
                    chips
                    deletable-chips
                    :rounded="false"
                    :solo="true"
                    clearable
                    dense
                    outlined
                    label="COFOG Classification"
                    x-small
                    :menu-props="{ maxWidth: '400', maxHeight: '400' }"
                    class="pr-2 cofog-combobox flex-grow-1"
                    style="min-height: 40px;"
                    @click:clear="clearCofogFilter()"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title :style="{ paddingLeft: (item.level - 1) * 20 + 'px' }">
                          {{ item.text }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                    <template v-slot:selection="{ attrs, item, select, selected }">
                      <v-chip
                        color="primary"
                        label
                        small
                        v-bind="attrs"
                        :input-value="selected"
                        close
                        @click="select"
                        @click:close="removeCofog(item)"
                        class="mr-1"
                      >
                        {{ item.text }}
                      </v-chip>
                    </template>
                  </v-combobox>
                </v-col>
                <!-- Government Type Filter -->
                <v-col cols="4" class="pa-0 ma-0 d-flex align-start">
                  <v-combobox
                    v-model="selectedGovTypes"
                    :items="gov_type_options"
                    item-text="text"
                    item-value="value"
                    return-object
                    x-small-chips
                    multiple
                    chips
                    deletable-chips
                    :rounded="false"
                    :solo="true"
                    clearable
                    dense
                    outlined
                    label="Government Type"
                    x-small
                    :menu-props="{ maxWidth: '400', maxHeight: '400' }"
                    class="pr-2 gov-combobox flex-grow-1"
                    style="min-height: 40px;"
                    @click:clear="clearGovTypeFilter()"
                  >
                    <template v-slot:selection="{ attrs, item, select, selected }">
                      <v-chip
                        color="primary"
                        label
                        small
                        v-bind="attrs"
                        :input-value="selected"
                        close
                        @click="select"
                        @click:close="removeGovType(item)"
                        class="mr-1"
                      >
                        {{ item.text }}
                      </v-chip>
                    </template>
                  </v-combobox>
                </v-col>
                <!-- Publisher Search -->
                <v-col cols="4" style="max-width:800px;" class="pa-0 ma-0 d-flex align-start">
                  <div class="flex-grow-1" style="min-height: 40px;">
                    <entity-profile-search :rounded="false" :color="'#f5f5f5'" :dark="false" :solo="true" :govts="selectedGovTypes.map(g => g.value)" @clearFilter="reset_filters" @search="handleSearch" />
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="12" class="pt-0 mb-0 pb-0 pr-0">
            <entity-table ref="entityTable" :useDirectoryAPI="true" />
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script>
import EntityProfileSearch from "../components/EntityProfileSearch";
import EntityTable from "../components/entities/EntityTable";
import { fetchCofogClassifications, fetchGovTypes } from "../components/APIManager";
import { getInstance } from "../auth";

export default {
  components: {
    EntityProfileSearch,
    EntityTable
  },
  props: {
    publisher: {}
  },
  data() {
    return {
      search: { publisher_id: Number },
      current_entity: {},
      global_search: false,
      view_index: null,
      selected: "snapshot",
      current_view: "snapshot",
      watching: false,
      filters: {},
      filters_changed: 0,
      selectedCofogs: [],
      cofog_options: [],
      cofog_options_formatted: [],
      selectedGovTypes: [],
      gov_type_options: []
    };
  },

  created() {
  },

  mounted() {
    // Load COFOG classifications
    this.loadCofogs();
    
    // Load government types
    const instance = getInstance();
    if (instance.isAuthenticated) {
      fetchGovTypes().then(results => {
        if (results && results.gov_types) {
          // Convert to format needed for the combobox
          this.gov_type_options = results.gov_types.map(gov => ({
            text: gov,
            value: gov
          }));
          
          // Load from route query (support both "government" and "gov_type" for compatibility)
          const qry = JSON.parse(JSON.stringify(this.$route.query));
          if (qry["government"]) {
            const govTypes = Array.isArray(qry["government"]) ? qry["government"] : [qry["government"]];
            this.selectedGovTypes = this.gov_type_options.filter(opt => 
              govTypes.includes(opt.value)
            );
          } else if (qry["gov_type"]) {
            const govTypes = Array.isArray(qry["gov_type"]) ? qry["gov_type"] : [qry["gov_type"]];
            this.selectedGovTypes = this.gov_type_options.filter(opt => 
              govTypes.includes(opt.value)
            );
          }
        }
      });
    }
    
    this.loadFiltersFromRoute();
  },

  methods: {
    loadCofogs() {
      fetchCofogClassifications()
        .then(response => {
          if (response.status === 'success' && response.data) {
            this.cofog_options = response.data;
            this.formatCofogOptions();
            this.loadFiltersFromRoute();
          }
        })
        .catch(error => {
          //console.error("Error loading COFOG classifications:", error);
        });
    },

    formatCofogOptions() {
      // Recursively format COFOG options with hierarchical indentation
      const formatCofog = (cofogList, level = 1) => {
        const items = [];
        cofogList.forEach(cofog => {
          const indent = '  '.repeat(level - 1);
          items.push({
            text: `${indent}${cofog.code} - ${cofog.name}`,
            value: cofog.code,
            level: cofog.level || level
          });
          
          // Recursively process children
          if (cofog.children && cofog.children.length > 0) {
            items.push(...formatCofog(cofog.children, level + 1));
          }
        });
        return items;
      };

      this.cofog_options_formatted = formatCofog(this.cofog_options);
    },

    loadFiltersFromRoute() {
      const query = this.$route.query;
      
      // Load COFOG selections
      if (query.cofog_code) {
        const cofogCodes = Array.isArray(query.cofog_code) ? query.cofog_code : [query.cofog_code];
        this.selectedCofogs = this.cofog_options_formatted.filter(opt => 
          cofogCodes.includes(opt.value)
        );
      }
      
      // Load gov_type selections (support both "government" and "gov_type" query params)
      if (query.gov_type || query.government) {
        const govTypes = Array.isArray(query.gov_type || query.government) 
          ? (query.gov_type || query.government)
          : [query.gov_type || query.government];
        this.selectedGovTypes = this.gov_type_options.filter(opt => 
          govTypes.includes(opt.value)
        );
      }
    },

    removeCofog(item) {
      const index = this.selectedCofogs.findIndex(c => c.value === item.value);
      if (index > -1) {
        this.selectedCofogs.splice(index, 1);
        this.applyFilters();
      }
    },

    removeGovType(item) {
      const index = this.selectedGovTypes.findIndex(g => g.value === item.value);
      if (index > -1) {
        this.selectedGovTypes.splice(index, 1);
        this.applyFilters();
      }
    },

    clearCofogFilter() {
      this.selectedCofogs = [];
      this.applyFilters();
    },

    clearGovTypeFilter() {
      this.selectedGovTypes = [];
      this.applyFilters();
    },

    reset_filters() {
      var qry = JSON.parse(JSON.stringify(this.$route.query));
      delete qry["term"];
      delete qry["page"];
      delete qry["cofog_code"];
      delete qry["gov_type"];
      delete qry["government"]; // Remove old format too
      this.$router.push({ query: qry });
    },

    handleSearch(queryFromSearch) {
      const newQuery = JSON.parse(JSON.stringify(queryFromSearch));

      // Merge COFOG classifications
      const cofogs = this.selectedCofogs && this.selectedCofogs.length > 0
        ? this.selectedCofogs.map(c => c.value)
        : null;
      
      if (cofogs) {
        newQuery["cofog_code"] = cofogs;
      } else {
        delete newQuery["cofog_code"];
      }

      // Merge gov_type (map to both gov_type and government for compatibility)
      const govTypes = this.selectedGovTypes && this.selectedGovTypes.length > 0
        ? this.selectedGovTypes.map(g => g.value)
        : null;
      
      if (govTypes) {
        newQuery["gov_type"] = govTypes;
        // Also set "government" for backward compatibility with existing routes
        newQuery["government"] = govTypes;
      } else {
        delete newQuery["gov_type"];
        delete newQuery["government"];
      }

      // Update route with merged query
      this.$router.replace({ name: this.$route.name, query: newQuery }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          //console.error(err);
        }
      });

      // Always trigger EntityTable reload
      if (this.$refs.entityTable) {
        this.$nextTick(() => {
          this.$refs.entityTable.buildQuery();
        });
      }
    },

    applyFilters() {
      const currentQuery = JSON.parse(JSON.stringify(this.$route.query));
      const newQuery = { ...currentQuery, page: 1 };

      // Add COFOG codes
      if (this.selectedCofogs && this.selectedCofogs.length > 0) {
        newQuery["cofog_code"] = this.selectedCofogs.map(c => c.value);
      } else {
        delete newQuery["cofog_code"];
      }

      // Add gov_type (also set "government" for backward compatibility)
      if (this.selectedGovTypes && this.selectedGovTypes.length > 0) {
        newQuery["gov_type"] = this.selectedGovTypes.map(g => g.value);
        newQuery["government"] = this.selectedGovTypes.map(g => g.value);
      } else {
        delete newQuery["gov_type"];
        delete newQuery["government"];
      }

      this.$router.push({ query: newQuery }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          //console.error(err);
        }
      });
    },

    isEmpty(obj) {
      for (var key in obj) {
        if (obj.hasOwnProperty(key)) return false;
      }
      return true;
    }
  },

  watch: {
    $route(to, from) {
      if (!from || !from.name) {
        return;
      }

      this.loadFiltersFromRoute();

      if (this.$refs.publisherTable) {
        this.$nextTick(() => {
          this.$refs.publisherTable.buildQuery();
        });
      }
    }
  },

  computed: {
    optionsLoaded() {
      return this.gov_type_options.length > 0;
    }
  }
}
</script>

<style scoped>
.align-top-row {
  align-items: flex-start !important;
}

.cofog-combobox >>> .v-input__control,
.gov-combobox >>> .v-input__control,
.publisher-search-wrapper >>> .search-combobox >>> .v-input__control {
  min-height: 40px;
}

.cofog-combobox >>> .v-input__slot,
.gov-combobox >>> .v-input__slot,
.publisher-search-wrapper >>> .search-combobox >>> .v-input__slot {
  min-height: 40px;
  align-items: flex-start;
  padding-top: 4px;
}
</style>
